import { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell as TableCellOrig,
  TableHead as TableHeadOrig,
  TableHeader,
  TableRow,
  TableLoading
} from "@/components/ui/table";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { Route as ReportAvailableRoute } from "../index";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { CSVLink } from "react-csv";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import PaginationButton from "@/components/ui/pagination-button";
import { cn } from "@/lib/utils";
import { usePLReportData } from "../-hooks/usePLReportData";

interface CSVDataItem {
  receipt_number: string;
  created_at: string;
  patient_name: string;
  invoice_amount: number;
  cogs: number;
  profit_percentage: string;
}

const TableCell = ({ className = "", ...props }) => (
  <TableCellOrig className={cn(tableCellClass, className)} {...props} />
);

const TableHead = ({ className = "", ...props }) => (
  <TableHeadOrig className={cn(tableHeadClass, className)} {...props} />
);

const columns = [
  { key: "receipt_number", label: "Receipt Number" },
  { key: "created_date", label: "Created Date" },
  { key: "patient_name", label: "Patient Name" },
  { key: "invoice_amount", label: "Invoice Amount" },
  { key: "invoice_amount_excl", label: "Invoice Amount (Excl Tax)" },
  { key: "cogs", label: "Cost of Goods Sold" },
  { key: "profit_percentage", label: "Profit Percentage" },
];
const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);

const Component = () => {
  const searchParams = ReportAvailableRoute.useSearch();
  const { data, isLoading, error, isFetching } = usePLReportData();
  const navigate = useNavigate();

  const [csvData, setCSVData] = useState<CSVDataItem[]>([]);

  const headers = [
    { label: "Receipt Number", key: "receipt_number" },
    { label: "Created Date", key: "created_at" },
    { label: "Patient Name", key: "patient_name" },
    { label: "Invoice Amount", key: "invoice_amount" },
    { label: "Cost of Goods Sold", key: "cogs" },
    { label: "Profit Percentage", key: "profit_percentage" },
  ];

  const totalPages = data?.totalCount && data.totalCount > 0 ? Math.ceil(data.totalCount / searchParams.pageSize) : 1;

  useEffect(() => {
    if (data?.totalCount && data.totalCount > 0) {
      const csvDataItems: CSVDataItem[] = data.items.map((item) => ({
        receipt_number: item.receiptNumber,
        created_at: dayjs(item.createdAt?.toDate()).format("DD/MM/YYYY"),
        patient_name: item.patientName,
        invoice_amount: item.grandTotalWithTax,
        cogs: item.totalCogs,
        profit_percentage: item.grossProfit / item.invoiceAmountBeforeTax * 100 + "%",
      }));
      setCSVData(csvDataItems);
    }
  }, [data]);

  const filename = "exported_data.csv";

  return (
    <>
      <div className="overflow-y-scroll h-screen pb-48">
        <div className="flex items-center justify-between">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => {
                    navigate({
                      to: "/branch/reports",
                    });
                  }}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  Reports Overview
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>PL Report</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <CSVLink data={csvData} headers={headers} filename={filename}>
            <Button>Export List</Button>
          </CSVLink>
        </div>
        <hr className="my-4" />

        <div className="border rounded-lg bg-white overflow-hidden">
          {isLoading ? (
            <TableLoading columns={columns} visibleColumns={visibleColumns} />
          ) : !data?.items || data.items.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-sm text-gray-500">No entries found</div>
            </div>
          ) : (
            <>
            <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <TableHead>Receipt Number</TableHead>
                <TableHead>Created Date</TableHead>
                <TableHead>Patient Name</TableHead>
                <TableHead>Invoice Amount</TableHead>
                <TableHead>Invoice Amount (Excl Tax)</TableHead>
                <TableHead>Cost of Goods Sold</TableHead>
                <TableHead>Profit Percentage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item, index) => (
                <TableRow key={index} className="font-medium">
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {item.receiptNumber}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {dayjs(item.createdAt?.toDate()).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {item.patientName}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {convertToCurrencyRounded(item.grandTotalWithTax)}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {convertToCurrencyRounded(item.invoiceAmountBeforeTax)}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.totalCogs)}</TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {(item.grossProfit / item.invoiceAmountBeforeTax * 100).toFixed(2)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>
          )}
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
            <p>{data?.items && data.items.length > 0 ? data.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: 1
                    }
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <DoubleArrowLeftIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page - 1
                    }
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationButton>
              {(() => {
                
                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (searchParams.page > 3) pages.push('...');
                    for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                      pages.push(i);
                    }
                    if (searchParams.page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                return getPageNumbers().map((page, idx) => (
                  page === '...'
                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                    : <PaginationButton
                        key={page}
                        active={searchParams.page === page}
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: typeof page === 'number' ? page : searchParams.page
                            }
                          });
                        }}
                      >
                        <span className="text-sm">{page}</span>
                      </PaginationButton>
                ));
              })()}
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page + 1
                    }
                  });
                }}
                disabled={searchParams.page >= totalPages}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: totalPages
                    }
                  });
                }}
                disabled={searchParams.page >= totalPages}
              >
                <DoubleArrowRightIcon className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Component;
